/**
 * Pure functions for stress-related calculations
 * Extracted for testability and separation of concerns
 */

import { hasAncestryFeature } from './featureDetection';

/**
 * Check if character has Firbolg Unshakable feature
 * @param {Object} character - Character object
 * @returns {boolean} True if character has Unshakable
 */
export const hasUnshakable = (character) => {
  if (!character) {
    return false;
  }

  return hasAncestryFeature(character, 'Unshakable');
};

/**
 * Apply Firbolg Unshakable feature when marking stress
 * Rolls d6 for each stress being marked. On a 6, that stress is avoided.
 *
 * @param {number} currentStress - Current stress value
 * @param {number} newStress - New stress value being set
 * @param {Object} character - Character object
 * @param {Function} onRoll - Optional callback for each roll (for UI feedback)
 * @returns {number} Actual stress value after Unshakable rolls
 */
export const applyUnshakable = (currentStress, newStress, character, onRoll) => {
  // Only apply if character has Unshakable
  if (!hasUnshakable(character)) {
    return newStress;
  }

  // Only apply if stress is increasing
  if (newStress <= currentStress) {
    return newStress;
  }

  // Calculate how many stress marks are being added
  const stressBeingAdded = newStress - currentStress;
  let stressAvoided = 0;

  // Roll d6 for each individual stress mark
  for (let i = 0; i < stressBeingAdded; i++) {
    const roll = Math.ceil(Math.random() * 6);

    if (roll === 6) {
      stressAvoided++;
    }

    // Call callback for UI feedback (e.g., toast notifications)
    if (onRoll) {
      onRoll(roll, roll === 6);
    }
  }

  // Return the actual stress after avoiding some via Unshakable
  const actualStress = newStress - stressAvoided;
  return actualStress;
};
